/*!
 * signalTriggers.js — firing a cue when a signal crosses a threshold
 * © 2025 Rob Canning — GPLv3
 *
 *   trig:"adc.onset>0.5"     fire when an attack arrives
 *   trig:"fader1.t>=0.8"     fire when a fader is pushed past 0.8
 *   trig:"adc.pitch>440"     fire when the sung note goes above A4
 *
 * The gap this fills: `if:` GATES a cue that something else is already firing.
 * Nothing could make a signal *cause* a firing. So an audio onset — the most
 * obviously event-like thing the analyser produces — could not start anything,
 * because adc.onset is a 0-1 envelope rather than a bang.
 *
 * RISING EDGE, NOT LEVEL. The condition is evaluated on every publish, which
 * for audio is 60 times a second. Firing while it merely *holds* true would
 * re-trigger a cue sixty times through one clap. A cue fires when the condition
 * goes from false to true, and is re-armed when it goes back to false.
 *
 * Quoting is not optional: the lexer does not read > or < inside an unquoted
 * parameter value, so trig:adc.onset>0.5 fails to parse. Same rule as `if:`.
 */

import { subscribe, get } from "./paramBus.js";
import { parseCondition, looksLikeCondition } from "../parser/parserSignalRef.js";
import { onReset } from "../system/teardown.js";

/** uid or element → unsubscribe, so re-assigning a score does not stack watchers. */
const _watchers = new Map();
/** A console line's watcher ("live:..." key) -> the line as written, for the console's marks. */
const _sources = new Map();
const _hits = new Map();       // key -> the hits counted (seeded rolls: the projector counts the same)
// a console line's watcher came or went: its line's "active" mark follows
const liveChanged = (id) => {
  if (typeof id === "string" && id.startsWith("live:") && typeof window.dispatchEvent === "function") window.dispatchEvent(new CustomEvent("oscilla:sound-state"));
};

/**
 * Does this trig: value describe a signal threshold rather than a mode or a uid?
 * Kept permissive — parseCondition is the authority on whether it really is one.
 */
export function isSignalTrig(v) {
  return typeof v === "string" && looksLikeCondition(v);
}

/** Apply a parsed condition to a value. Mirrors evaluateCondition in the dispatcher. */
function holds(cond, value) {
  if (!Number.isFinite(value)) return false;
  if (cond.op == null) return value > 0;          // truthy form: "adc.onset"
  switch (cond.op) {
    case ">":  return value > cond.threshold;
    case "<":  return value < cond.threshold;
    case ">=": return value >= cond.threshold;
    case "<=": return value <= cond.threshold;
    case "==": return Math.abs(value - cond.threshold) < 0.0001;
    case "!=": return Math.abs(value - cond.threshold) >= 0.0001;
    default:   return false;
  }
}

/**
 * Watch `expr` and call `fire` each time it becomes true.
 *
 * @param {string} expr   e.g. "adc.onset>0.5"
 * @param {Function} fire called on each rising edge
 * @param {Object} [opts]
 * @param {*} [opts.key]  identity for replacing an existing watcher (the element)
 * @param {boolean} [opts.once] stop after the first firing
 * @param {number} [opts.hits] the hits counted already (a projector window
 *   joining late is given the laptop's): fire gets each hit's number, from it
 * @returns {Function|null} unsubscribe, or null if the expression is not a condition
 */
export function registerSignalTrigger(expr, fire, { key = null, once = false, source = null, hits = 0 } = {}) {
  const cond = parseCondition(String(expr));
  if (!cond?.signal) {
    console.warn(`[signalTrig] not a condition: "${expr}"`);
    return null;
  }

  const id = key ?? expr;
  _watchers.get(id)?.();                      // replace, never stack

  // Armed state starts from whatever the signal already holds, so a score
  // loaded while the condition is already true does not fire immediately.
  let armed = !holds(cond, get(cond.signal));
  let done = false;
  let hit = hits;

  const unsub = subscribe(cond.signal, (value) => {
    if (done) return;
    const now = holds(cond, value);
    if (now && armed) {
      armed = false;
      if (once) stop();
      _hits.set(id, hit + 1);
      try { fire(value, hit++); }
      catch (err) { console.error(`[signalTrig] "${expr}" handler failed:`, err); }
    } else if (!now && !armed) {
      armed = true;                            // fell back below: re-arm
    }
  });

  const stop = () => {
    done = true; unsub();
    if (_watchers.get(id) === stop) { _watchers.delete(id); _sources.delete(id); _hits.delete(id); liveChanged(id); }
  };
  _watchers.set(id, stop);
  if (source != null) _sources.set(id, source); else _sources.delete(id);
  _hits.set(id, hit);
  liveChanged(id);
  return stop;
}

/** Drop the watcher for one key — used when an element's DSL is rewritten. */
export function dropSignalTrigger(key) {
  const stop = _watchers.get(key);
  if (stop) stop();
}

/** Drop the watchers whose key starts with `prefix` -- the live console's ("live:"), at hush(). */
export function dropSignalTriggers(prefix) {
  for (const [k, stop] of [..._watchers]) if (typeof k === "string" && k.startsWith(prefix)) stop();
}

/** Drop every watcher. Called when a score is unloaded. */
export function resetSignalTriggers() {
  for (const stop of [..._watchers.values()]) stop();
  _watchers.clear();
  _sources.clear();
  _hits.clear();
}

/**
 * The console's lines running on a signal: [{ key, type, el, who, source, hits }]
 * from their "live:<cue type>:<element id>:<name>" keys -- what the console
 * marks "active", and what Ctrl+Enter on an unchanged one switches off.
 */
export function liveSignalTriggers() {
  const out = [];
  for (const key of _watchers.keys()) {
    if (typeof key !== "string" || !key.startsWith("live:")) continue;
    const [, type = "", el = "", who = ""] = key.split(":");
    out.push({ key, type, el, who, source: _sources.get(key) ?? null, hits: _hits.get(key) ?? 0 });
  }
  return out;
}

/** The console lines on a signal that draw, not sound (the type in their key). */
const DRAWS = new Set([
  "cueText", "text", "cueShape", "cueUse", "cueViz", "cueRotate", "cueScale", "cueTrans",
  "cueColor", "cueO2P", "cueDraw", "cueZoom", "cueScreen", "cueImage", "cueScatter", "cueGrid", "cueStrobe",
]);

/** still(): the console's trig: lines that draw stop watching; a sound's plays on. */
export function dropDrawingTriggers() {
  for (const { key, type } of liveSignalTriggers()) if (DRAWS.has(type)) dropSignalTrigger(key);
}

// hush() lets go of every console trig: line, still(all) of those that draw,
// a project switch of every watcher
onReset("sound", function dropConsoleTriggers() { dropSignalTriggers("live:"); });
onReset("stage", dropDrawingTriggers);
onReset("project", resetSignalTriggers);
